import {useEffect, useCallback, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {useAuthClient} from './use-auth-client'
import {useLocation} from 'react-router'
import {
  MC_PLANS,
  BC_PLANS,
  RC_PLANS,
  WC_PLANS,
  KEAP_MAX_PLANS,
  SOCIAL_ADS_5_CODES,
  SOCIAL_ADS_4_CODES,
  NEW_CUSTOMER_INIT_MC,
  NEW_CUSTOMER_INIT_BC,
} from './constants'
import {ADD_ON} from './addons-data'
import {
  selectManualCoupons,
  selectAutoCoupons,
  selectDefaultCoupons,
  selectMergedCoupons,
  selectIncentiveCoupon,
  selectAppliedCouponsFlag,
  selectUpgradePaymentPreview,
  setAutoCoupons,
  clearAutoCoupons,
  setIncentiveCoupon as setIncentiveCouponAction,
  setAppliedCouponsFlag,
} from '../app-store/coupon'

const WIN_BACK_4_COUPONS = {
  'plus-4-dexyp-m2m': 'pluswinback2022',
  'unlimited-4-dexyp-m2m': 'unlimitedwinback2022',
  'professional-4-dexyp-m2m': 'professionalwinback2022',
}

function useAutoCoupons({
  order, //FULSAS-3648
  accountInfo, //FULSAS-3648
  selectedPackage,
  selectedIndustryId,
  selectedCountry,
  selectedCentersAndApps,
  defaultCentersAndApps,
  expiredCentersAndApps,
  eligibleTransitionCenters,
  selectedAddons,
  upgradeAccountInfo,
  currentStep,
  setOrderCouponCodes,
  isEaidFound,
  isCohort,
  clientType,
}) {
  const dispatch = useDispatch()
  const ccCouponInfo = useSelector(selectManualCoupons, shallowEqual)
  const autoCouponInfo = useSelector(selectAutoCoupons, shallowEqual)
  const defaultCouponInfo = useSelector(selectDefaultCoupons, shallowEqual)
  const orderCouponCodes = useSelector(selectMergedCoupons, shallowEqual)
  const incentiveCoupon = useSelector(selectIncentiveCoupon)
  const appliedCoupons = useSelector(selectAppliedCouponsFlag)
  const upgradePaymentPreview = useSelector(selectUpgradePaymentPreview)
  const client = useAuthClient()
  const {pathname} = useLocation()

  console.log('use-auto-coupon.js ccCouponInfo :: ', ccCouponInfo)
  console.log('use-auto-coupon.js defaultCouponInfo :: ', defaultCouponInfo)
  const isNewOrder =
    pathname === '/order/new' &&
    selectedIndustryId !== 13 &&
    selectedIndustryId !== 12
  const isUpgrade = pathname === '/order/upgrade'
  const selectedPlan = selectedPackage || 'appmarket-5-thryv-m2m'
  const is5Plan = selectedPlan.includes('-5-')
  const FREE_ACCOUNT_CODES = [
    'appmarket-5-thryv-m2m',
    'cc_basicpls-5-thryv-m2m',
    'cc_basic-5-thryv-m2m',
    'addon-5-signatures_usage',
    'app-websitebuilder',
  ]
  // const MC_PLAN_CODES = ['addon-4-mc-m2m', 'addon-4-mcpro-m2m']
  const findSensisSiteDomain = () => {
    if (selectedCentersAndApps) {
      return selectedCentersAndApps.apps.find(app => app.sensisSiteDomain)
        ?.sensisSiteDomain
    }
    return selectedAddons?.find(
      x => x.product === ADD_ON.add_ons['Website'].label,
    )?.sensisSiteDomain
  }
  const resetAutoCoupons = useCallback(() => {
    dispatch(clearAutoCoupons())
    dispatch(setAppliedCouponsFlag(false))
  }, [dispatch])

  // FULSAS-11343: the merge effect below only ever appends coupons to
  // orderCouponCodes (mergedCoupons) - it never removes one, and it skips
  // running entirely when there are zero applicable coupons. So when a user
  // removes the last manual coupon on the Catalog page, mergedCoupons never
  // drops it and stale discounts keep showing on Due Today. Track manual
  // coupon removals directly and strip them out regardless of step/flag.
  const prevManualCouponsRef = useRef(ccCouponInfo)
  useEffect(() => {
    const prevCodes = prevManualCouponsRef.current.map(coupon => coupon.code)
    const currentCodes = ccCouponInfo.map(coupon => coupon.code)
    const removedCodes = prevCodes.filter(code => !currentCodes.includes(code))

    if (removedCodes.length > 0) {
      setOrderCouponCodes(prevData =>
        prevData.filter(coupon => !removedCodes.includes(coupon.code)),
      )
    }

    prevManualCouponsRef.current = ccCouponInfo
  }, [ccCouponInfo, setOrderCouponCodes])

  const removeDuplicatesFromArray = (arrayObject, comparisionAttribute) => {
    const uniqueArrayElements = new Set()
    return arrayObject.filter(item => {
      const keyValue = item[comparisionAttribute]
      return uniqueArrayElements.has(keyValue)
        ? false
        : (uniqueArrayElements.add(keyValue), true)
    })
  }
  useEffect(() => {
    if (
      (currentStep < 6 && !isUpgrade) ||
      (isUpgrade &&
        ((currentStep < 2 && is5Plan) || (currentStep < 3 && !is5Plan)))
    )
      return

    async function fetchCoupon({couponCode, planCode, addonsString}) {
      const baseUrl = 'orders/coupon/validate'
      const addonsQueryString = addonsString
        ? `addonsString=${addonsString}&`
        : ''
      const url = `${baseUrl}?${addonsQueryString}couponCode=${couponCode}&country=${selectedCountry.isoValue}&planCode=${planCode}`
      const coupon = await client(url)
      return coupon
    }

    async function applyAutoCoupons() {
      let isFreeAccountWithoutExpiredSubs = true
      const isHomeServicesInDefault =
        defaultCentersAndApps?.current?.apps?.some(app =>
          [
            'maxhvac-5-thryv-m2m',
            'maxroof-5-thryv-m2m',
            'maxelect-5-thryv-m2m',
            'maxplumb-5-thryv-m2m',
          ].includes(app.code),
        )
      const isHomeServicesInCart = selectedCentersAndApps?.apps?.find(item =>
        [
          'maxhvac-5-thryv-m2m',
          'maxroof-5-thryv-m2m',
          'maxelect-5-thryv-m2m',
          'maxplumb-5-thryv-m2m',
        ].includes(item.code),
      )
      const isFreeAccount = !(
        defaultCentersAndApps?.current?.centers?.some(
          center => !FREE_ACCOUNT_CODES.includes(center.code),
        ) ||
        defaultCentersAndApps?.current?.apps?.some(
          app => !FREE_ACCOUNT_CODES.includes(app.code),
        )
      )
      if (isUpgrade) {
        isFreeAccountWithoutExpiredSubs = !(
          expiredCentersAndApps?.current?.centers?.some(
            center => !FREE_ACCOUNT_CODES.includes(center.code),
          ) ||
          expiredCentersAndApps?.current?.apps?.some(
            app => !FREE_ACCOUNT_CODES.includes(app.code),
          )
        )
      }
      console.log(
        'isFreeAccountWithoutExpiredSubs' + isFreeAccountWithoutExpiredSubs,
      )
      const codes = []
      for (const key in selectedCentersAndApps) {
        codes.push(...selectedCentersAndApps[key].map(x => x.code))
      }
      if (!is5Plan) {
        codes.push(...selectedAddons.map(item => item.planCode))
        // const isMCIncluded = upgradeAccountInfo.addOns.some(({addOnCode}) =>
        //   MC_PLAN_CODES.includes(addOnCode),
        // )
        // const isMCInCart = selectedAddons.some(({planCode}) =>
        //   MC_PLAN_CODES.includes(planCode),
        // )
        // if (isMCInCart && !isMCIncluded) {
        //   const mcAddOnCode = selectedAddons.find(
        //     ({planCode}) =>
        //       planCode === 'addon-4-mc-m2m' || planCode === 'addon-4-mcpro-m2m',
        //   )?.planCode
        //   codes.push(mcAddOnCode)
        // }
      }

      let response = null

      try {
        response = await client(
          `cc/order/fetchPlanCoupons/${
            selectedCountry.isoValue
          }/${codes.join()}/${clientType}/${
            order?.CCAccountInfo?.thryvId || accountInfo?.thryvID || 'null'
          }`,
        )
      } catch (err) {
        console.error(err)
      }

      let autoCouponData = []

      if (response) {
        if (clientType !== 'keap') {
          //check for keap-nci-mc coupon inthe responseand if present remove it
          response = response.filter(item => item.coupon_code !== 'keap-nci-mc')
        }
        // Filter coupon that are incompatible with selected non-default coupon
        // let incompatibleCoupons = ccCouponInfo?.incompatibles?.split(',')
        let incompatibleCoupons =
          ccCouponInfo.reduce(
            (incompatibles, coupon) =>
              coupon?.incompatibles && coupon?.incompatibles !== 'null'
                ? coupon?.incompatibles + ',' + incompatibles
                : incompatibles,
            '',
          ).length > 0
            ? ccCouponInfo
                .reduce(
                  (incompatibles, coupon) =>
                    coupon?.incompatibles && coupon?.incompatibles !== 'null'
                      ? coupon?.incompatibles + ',' + incompatibles
                      : incompatibles,
                  '',
                )
                .split(',')
            : null
        if (incompatibleCoupons && incompatibleCoupons.length > 0) {
          response = response.filter(
            item =>
              !incompatibleCoupons.some(
                incompatibleCoupon => incompatibleCoupon === item.coupon_code,
              ),
          )
        }
        // for (let orderCouponCode of orderCouponCodes) {
        //   console.log('order coupon code is :: ', orderCouponCode)
        //   let incompCoupons = orderCouponCode?.incompatibles?.split(',')
        //   if (incompCoupons && incompCoupons.length > 0) {
        //     response = response.filter(
        //       item =>
        //         !incompCoupons.some(
        //           incompatibleCoupon => incompatibleCoupon === item.coupon_code,
        //         ),
        //     )
        //   }
        // }
        for (let coupon of response) {
          let couponApplicable = false
          if (coupon?.new_client_only_flag === 'Y') {
            //coupon is applicable only to new clients
            if (
              isFreeAccount &&
              isFreeAccountWithoutExpiredSubs &&
              coupon.new_auto_apply === 'Y'
            ) {
              // Add only coupon that is auto applied for new clients
              couponApplicable = true
            }
          } else {
            //coupon is applicable both new and existing clients
            if (
              isFreeAccount &&
              isFreeAccountWithoutExpiredSubs &&
              coupon.new_auto_apply === 'Y'
            ) {
              // Add only coupon that is auto applied for new client
              couponApplicable = true
            } else if (
              !(isFreeAccount && isFreeAccountWithoutExpiredSubs) &&
              coupon.existing_auto_apply === 'Y'
            ) {
              // Add only coupon that is auto applied for existing client
              couponApplicable = true
            } else {
              if (!is5Plan) {
                couponApplicable = true
              }
            }
          }
          if (!isHomeServicesInDefault && isHomeServicesInCart) {
            if (coupon?.coupon_code === 'nci-mc') {
              couponApplicable = false
            }
          }
          // FULSAS-8726: Cohort net new accounts qualify for the Cohort Wave
          // OB Fee incentive only and must not also receive the New Customer
          // Incentive (nci-mc / nci-bc).
          if (
            isCohort &&
            [NEW_CUSTOMER_INIT_MC, NEW_CUSTOMER_INIT_BC].includes(
              coupon?.coupon_code,
            )
          ) {
            couponApplicable = false
          }
          if (SOCIAL_ADS_5_CODES.includes(coupon?.plan_code)) {
            const socialAdsCode = coupon.plan_code
            const isInCart = selectedCentersAndApps?.apps?.some(
              ({code}) => code === socialAdsCode,
            )
            const isCouponAlreadyApplied = defaultCouponInfo?.some(
              c => c.code === coupon.coupon_code,
            )
            const hasExistingSocialAds5 =
              defaultCentersAndApps?.current?.apps?.some(({code}) =>
                SOCIAL_ADS_5_CODES.includes(code),
              )

            if (isInCart && !isCouponAlreadyApplied && !hasExistingSocialAds5) {
              const createUrl = `orders/coupon/validate?couponCode=${coupon.coupon_code}&country=${selectedCountry.isoValue}&planCode=${socialAdsCode}`

              const couponResponse = await client(createUrl)

              autoCouponData.push(couponResponse)
            }
          } else if (SOCIAL_ADS_4_CODES.includes(coupon?.plan_code)) {
            const socialAdsCode = coupon.plan_code
            const isInCart = selectedAddons?.some(
              ({planCode}) => planCode === socialAdsCode,
            )
            const isCouponAlreadyApplied = defaultCouponInfo?.some(
              c => c.code === coupon.coupon_code,
            )
            const hasExistingSocialAds4 = upgradeAccountInfo?.addOns?.some(
              ({addOnCode}) => SOCIAL_ADS_4_CODES.includes(addOnCode),
            )

            if (isInCart && !isCouponAlreadyApplied && !hasExistingSocialAds4) {
              const createUrl = `orders/coupon/validate?couponCode=${coupon.coupon_code}&country=${selectedCountry.isoValue}&planCode=${socialAdsCode}`
              const couponResponse = await client(createUrl)

              autoCouponData.push(couponResponse)
            }
          } else if (couponApplicable) {
            if (!is5Plan) {
              let createUrl
              if (
                coupon?.bundle_config &&
                coupon?.bundle_config.split(',').length > 0
              ) {
                createUrl = `orders/coupon/validate?couponCode=${
                  coupon?.coupon_code
                }&country=${selectedCountry.isoValue}&planCode=${
                  coupon?.bundle_config.split(',')[0]
                }`
              } else {
                createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
              }
              let couponResponse = await client(createUrl)
              autoCouponData.push(couponResponse)
            } else {
              let planPrefix = coupon?.plan_code?.slice(0, 2)
              if (planPrefix) {
                switch (planPrefix) {
                  case 'bc':
                    {
                      const isBCInCart = selectedCentersAndApps?.centers.some(
                        ({code}) => BC_PLANS.includes(code),
                      )
                      const isBCInDefault =
                        defaultCentersAndApps?.current?.centers.some(({code}) =>
                          BC_PLANS.includes(code),
                        )
                      const isBCCouponAlreadyApplied =
                        defaultCouponInfo?.filter(coupon => {
                          if (
                            coupon.appliesToPlans.some(
                              item =>
                                item === 'bc_plus-5-thryv-m2m' ||
                                item === 'bc_pro-5-thryv-m2m' ||
                                item === 'bc_unlm-5-thryv-m2m' ||
                                item === 'bc-6-thryv-m2m',
                            )
                          )
                            return true
                          else return false
                        })
                      if (
                        !isBCCouponAlreadyApplied ||
                        (isBCCouponAlreadyApplied &&
                          isBCCouponAlreadyApplied.length === 0)
                      ) {
                        if (isBCInCart) {
                          if (!isBCInDefault) {
                            const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
                            let couponResponse = await client(createUrl)
                            autoCouponData.push(couponResponse)
                          }
                        }
                      }
                    }
                    break
                  case 'mc':
                    {
                      const isMCInCart = selectedCentersAndApps?.centers?.some(
                        ({code}) => MC_PLANS.includes(code),
                      )
                      const isMCInDefault =
                        defaultCentersAndApps?.current?.centers?.some(
                          ({code}) => MC_PLANS.includes(code),
                        )
                      const isMCCouponAlreadyApplied =
                        defaultCouponInfo?.filter(coupon => {
                          if (
                            coupon.appliesToPlans.some(
                              item =>
                                item === 'mc_plus-5-thryv-m2m' ||
                                item === 'mc_pro-5-thryv-m2m',
                            )
                          )
                            return true
                          else return false
                        })
                      if (
                        !isMCCouponAlreadyApplied ||
                        (isMCCouponAlreadyApplied &&
                          isMCCouponAlreadyApplied.length === 0)
                      ) {
                        if (isMCInCart) {
                          if (!isMCInDefault) {
                            const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
                            let couponResponse = await client(createUrl)
                            console.log('couponResponse ::', couponResponse)
                            autoCouponData.push(couponResponse)
                          }
                        }
                      }
                    }
                    break
                  case 'rc':
                    {
                      const isRCInCart = selectedCentersAndApps?.centers.some(
                        ({code}) => RC_PLANS.includes(code),
                      )
                      const isRCInDefault =
                        defaultCentersAndApps?.current?.centers.some(({code}) =>
                          RC_PLANS.includes(code),
                        )
                      const isRCCouponAlreadyApplied =
                        defaultCouponInfo?.filter(coupon => {
                          if (
                            coupon.appliesToPlans.some(
                              item => item === 'rc_plus-5-thryv-m2m',
                            )
                          )
                            return true
                          else return false
                        })
                      if (
                        !isRCCouponAlreadyApplied ||
                        (isRCCouponAlreadyApplied &&
                          isRCCouponAlreadyApplied.length === 0)
                      ) {
                        if (isRCInCart) {
                          if (!isRCInDefault) {
                            const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
                            let couponResponse = await client(createUrl)
                            autoCouponData.push(couponResponse)
                          }
                        }
                      }
                    }
                    break
                  case 'wc':
                    {
                      const isWCInCart = selectedCentersAndApps?.centers.some(
                        ({code}) => WC_PLANS.includes(code),
                      )
                      const isWCInDefault =
                        defaultCentersAndApps?.current?.centers.some(({code}) =>
                          WC_PLANS.includes(code),
                        )
                      const isWCCouponAlreadyApplied =
                        defaultCouponInfo?.filter(coupon => {
                          if (
                            coupon.appliesToPlans.some(
                              item => item === 'wc_plus-5-thryv-m2m',
                            )
                          )
                            return true
                          else return false
                        })
                      if (
                        !isWCCouponAlreadyApplied ||
                        (isWCCouponAlreadyApplied &&
                          isWCCouponAlreadyApplied.length === 0)
                      ) {
                        if (isWCInCart) {
                          if (!isWCInDefault) {
                            const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
                            let couponResponse = await client(createUrl)
                            autoCouponData.push(couponResponse)
                          }
                        }
                      }
                    }
                    break
                  case 'kp':
                    {
                      const isZapierPromotion =
                        coupon?.plan_code === 'kp-zapier-750zaps-m2m'

                      const isThryvHVACorMaxInCart =
                        selectedCentersAndApps?.centers.some(
                          ({code}) =>
                            code.includes('hvac-5-thryv-m2m') ||
                            code.includes('keap_max-5-thryv-m2m'),
                        )
                      const isThryvHVACorMaxInDefault =
                        defaultCentersAndApps?.current?.centers.some(
                          ({code}) =>
                            code.includes('hvac-5-thryv-m2m') ||
                            code.includes('keap_max-5-thryv-m2m'),
                        )

                      if (
                        isZapierPromotion &&
                        isThryvHVACorMaxInCart &&
                        !isThryvHVACorMaxInDefault
                      ) {
                        const isThryvHVACInCart =
                          selectedCentersAndApps?.centers.some(({code}) =>
                            code.includes('hvac-5-thryv-m2m'),
                          )
                        const isKeapMaxInCart =
                          selectedCentersAndApps?.centers.some(({code}) =>
                            code.includes('keap_max-5-thryv-m2m'),
                          )
                        const planCodeToApply = isThryvHVACInCart
                          ? 'hvac-5-thryv-m2m'
                          : isKeapMaxInCart
                          ? 'keap_max-5-thryv-m2m'
                          : null

                        const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${planCodeToApply}`
                        let couponResponse = await client(createUrl)
                        autoCouponData.push(couponResponse)
                      }
                    }
                    break
                  case 'ke':
                    {
                      const isKeapMaxInCart =
                        selectedCentersAndApps?.centers.some(({code}) =>
                          KEAP_MAX_PLANS.includes(code),
                        )
                      const isKeapMaxInDefault =
                        defaultCentersAndApps?.current?.centers.some(({code}) =>
                          KEAP_MAX_PLANS.includes(code),
                        )
                      const isKeapMaxCouponAlreadyApplied =
                        defaultCouponInfo?.filter(coupon => {
                          if (
                            coupon.appliesToPlans.some(
                              item => item === 'keap_max-5-thryv-m2m',
                            )
                          )
                            return true
                          else return false
                        })
                      if (
                        !isKeapMaxCouponAlreadyApplied ||
                        (isKeapMaxCouponAlreadyApplied &&
                          isKeapMaxCouponAlreadyApplied.length === 0)
                      ) {
                        if (isKeapMaxInCart) {
                          if (!isKeapMaxInDefault) {
                            const createUrl = `orders/coupon/validate?couponCode=${coupon?.coupon_code}&country=${selectedCountry.isoValue}&planCode=${coupon?.plan_code}`
                            let couponResponse = await client(createUrl)
                            autoCouponData.push(couponResponse)
                          }
                        }
                      }
                    }
                    break
                  default:
                    break
                }
              } else {
                // process the bundle coupons
                let isBundleCouponAlreadyApplied = defaultCouponInfo?.filter(
                  defaultCoupon => defaultCoupon.code === coupon.code,
                )
                if (
                  !isBundleCouponAlreadyApplied ||
                  (isBundleCouponAlreadyApplied &&
                    isBundleCouponAlreadyApplied.length === 0)
                ) {
                  const createUrl = `orders/coupon/validate?couponCode=${
                    coupon?.coupon_code
                  }&country=${selectedCountry.isoValue}&planCode=${
                    coupon?.bundle_config.split(',')[0]
                  }`
                  let couponResponse = await client(createUrl)
                  autoCouponData.push(couponResponse)
                }
              }
            }
          }
        }
      }
      const isWebsiteApp =
        selectedCentersAndApps?.apps.find(
          ({name}) => name === 'Professionally Designed Website',
        ) || selectedAddons?.find(({product}) => product === 'Website')
      const isTrialPlan = upgradeAccountInfo?.planCode?.includes('trial')
      const sensisDomain = findSensisSiteDomain()
      const addonsString =
        (!selectedCentersAndApps
          ? selectedAddons
              ?.filter(addon => addon.planCode !== undefined)
              ?.map(addon => addon.planCode)
          : [
              ...selectedCentersAndApps.centers,
              ...selectedCentersAndApps.apps,
              ...selectedCentersAndApps.oneTimes,
            ]
              .map(obj => obj.code)
              .join(',')) || ''
      let incentive = incentiveCoupon
      let data

      if (!isNewOrder && upgradeAccountInfo?.status !== 'active') {
        const winBackCoupon = WIN_BACK_4_COUPONS[selectedPlan]
        if (winBackCoupon)
          data = {couponCode: winBackCoupon, planCode: selectedPlan}
      } else if (isTrialPlan) {
        data = {
          couponCode: 'transition75',
          planCode: selectedPlan,
        }
      }

      let domainData

      if ((isWebsiteApp?.name || isWebsiteApp?.product) && sensisDomain) {
        domainData = {
          couponCode: 'urldbdesignfee',
          planCode: selectedPlan,
        }
      }

      const couponInfo = data && (await fetchCoupon({...data, addonsString}))

      const domainCoupon =
        domainData && (await fetchCoupon({...domainData, addonsString}))

      const cohortCouponApplies =
        isCohort &&
        (selectedCentersAndApps?.oneTimes.find(
          fee => fee.code === 'thryvprfee',
        ) ||
          selectedAddons?.find(addon => addon.planCode === 'onboarding-fee'))

      const cohortCoupon =
        cohortCouponApplies &&
        (await fetchCoupon({
          couponCode: 'cohort-waive-onboardingfee',
          addonsString,
        }))

      if (incentive) dispatch(setIncentiveCouponAction(couponInfo))

      if (couponInfo) autoCouponData.push(couponInfo)
      if (domainCoupon) autoCouponData.push(domainCoupon)
      if (cohortCoupon) autoCouponData.push(cohortCoupon)

      // ----------------- FULSAS-3648 filter starts -----------------
      console.log('autoCouponData :: ', autoCouponData)
      let finalAutoCoupons = []
      if (isUpgrade) {
        const thryvIdForCoupons =
          order?.CCAccountInfo?.thryvId || accountInfo?.thryvID
        if (thryvIdForCoupons) {
          const activeCoupons = await client(
            `accounts/5/coupon-redemptions?thryvId=${thryvIdForCoupons}`,
          )
          let activeCouponCodes = activeCoupons.map(coupon => coupon.code)
          let oneTimeRedemptionCoupons = autoCouponData.filter(
            coupon => coupon.maxRedemptionsPerAccount === 1,
          )
          if (oneTimeRedemptionCoupons && oneTimeRedemptionCoupons.length > 0) {
            finalAutoCoupons = autoCouponData.filter(
              autoCoupon =>
                !activeCouponCodes.some(
                  couponToBeRemoved => couponToBeRemoved === autoCoupon.code,
                ),
            )
          } else {
            finalAutoCoupons = autoCouponData
          }
        } else {
          finalAutoCoupons = autoCouponData
        }
      } else {
        finalAutoCoupons = autoCouponData
      }
      // ------------------ FULSAS-3648 filter ends ------------------
      if (finalAutoCoupons.length > 0)
        dispatch(setAutoCoupons(finalAutoCoupons))
    }
    console.log(
      'ccCouponInfo before calling applyAutoCoupons is  :: ',
      JSON.stringify(ccCouponInfo),
    )
    console.log(applyAutoCoupons)
    applyAutoCoupons()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, upgradePaymentPreview])

  useEffect(() => {
    let incompatibles
    let uniqueIncompatibles = []
    let incompatibleCoupons = []
    let filteredOrderCouponCodes = []

    for (let coupon of autoCouponInfo) {
      incompatibles =
        incompatibles +
        (coupon?.incompatibles?.length > 0 ? coupon.incompatibles : '')
    }
    if (
      incompatibles &&
      incompatibles.length > 0 &&
      incompatibles.split(',') &&
      incompatibles.split(',').length > 0
    ) {
      incompatibleCoupons = incompatibles.split(',')
      uniqueIncompatibles = [...new Set(incompatibleCoupons)]
    }
    console.log('uniqueIncompatibles : ', uniqueIncompatibles)

    filteredOrderCouponCodes = orderCouponCodes.filter(item =>
      uniqueIncompatibles.includes(item.coupon_code),
    )
    console.log('filteredOrderCouponCodes :', filteredOrderCouponCodes)

    if (autoCouponInfo && autoCouponInfo.length > 0)
      setOrderCouponCodes(prevData =>
        removeDuplicatesFromArray(
          [
            ...prevData.filter(
              item => !uniqueIncompatibles.includes(item.code),
            ),
            ...autoCouponInfo,
          ],
          'code',
        ),
      )
  }, [autoCouponInfo, orderCouponCodes, setOrderCouponCodes])

  useEffect(() => {
    console.log('Inside useEffect of setOrderCouponCodes')
    if (
      ((currentStep < 6 && !isUpgrade) ||
        (isUpgrade && ((currentStep < 2 && is5Plan) || !is5Plan))) &&
      appliedCoupons
    )
      return

    console.log('defaultCouponInfo :: ', defaultCouponInfo)

    console.log('Merged array :: ', [
      ...[
        ...autoCouponInfo,
        // ccCouponInfo?.isValid ? ccCouponInfo : null,
        ...ccCouponInfo,
      ].filter(Boolean),
      ...(defaultCouponInfo ? defaultCouponInfo : []),
    ])
    if (isUpgrade) {
      let allCoupons = removeDuplicatesFromArray(
        [
          ...[...autoCouponInfo, ...ccCouponInfo].filter(Boolean),
          ...(defaultCouponInfo ? defaultCouponInfo : []),
        ],
        'code',
      )
      if (allCoupons && allCoupons.length > 0) {
        setOrderCouponCodes(prevData =>
          removeDuplicatesFromArray([...prevData, ...allCoupons], 'code'),
        )
      }
    } else {
      let allCoupons = removeDuplicatesFromArray(
        [
          ...[...autoCouponInfo, ...ccCouponInfo].filter(Boolean),
          ...(defaultCouponInfo ? defaultCouponInfo : []),
        ],
        'code',
      )

      if (allCoupons && allCoupons.length > 0) {
        setOrderCouponCodes(prevData =>
          removeDuplicatesFromArray([...prevData, ...allCoupons], 'code'),
        )
      }
    }
    dispatch(setAppliedCouponsFlag(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoCouponInfo,
    ccCouponInfo,
    defaultCouponInfo,
    setOrderCouponCodes,
    currentStep,
    appliedCoupons,
  ])
  console.log('autoCouponInfo', autoCouponInfo)
  return {autoCouponInfo, resetAutoCoupons}
}

export default useAutoCoupons
